<template>
<div class="post-container">
    <div class="top">
        <img v-if="articleDetails != null && articleDetails.picture_url != null" :src="articleDetails.picture_url" class="hero-img" />
        <div class="header">
            <div class="sx">
                <h1 style="margin: 0; display: flex; align-items: center;">
                    {{ articleDetails != null ? articleDetails.title : '..' }}
                    <!-- Edit button disabled: blog is now managed via WordPress -->
                    <img v-if="user != null && user.is_admin" style="width: 20px; cursor: not-allowed; opacity: 0.3;" src="../../assets/icons/edit.png" title="Editing disabilitato — gestisci dal pannello WordPress" />
                </h1>
                <p style="margin: 0;" class="light" v-if="!$parent.$parent.isMobile">
                    {{ articleDetails != null ? (new Date(articleDetails.updated_at)).toLocaleDateString() : '..' }}
                </p>
            </div>
            <div class="dx" v-if="articleDetails != null">
                <vs-button v-for="tag in articleDetails.tags" :key="'tag-' + tag" dark>#{{ tag }}</vs-button>
            </div>
        </div>

    </div>

    <template v-if="articleDetails != null">
        <div class="b-content" v-html="DOMPurify.sanitize(articleDetails.text, {ADD_TAGS: ['iframe','video']})"></div>
    </template>

</div>
</template>

<script>
import axios from 'axios';
import DOMPurify from "dompurify";
import getEnv from '../../utils/env.js';
import { toWpmlLocale, localeReadiness } from '../../utils/wpmlLocale.js';

function wpBaseUrl() {
    return (getEnv('WP_BLOG_URL') || 'https://tourial.it/blog').replace(/\/+$/, '');
}

export default {
    name: "post",

    props: {
        user: {
            type: Object,
            default: null
        },

        deleted: {
            type: Function,
            required: false
        }
    },

    data() {
        return {
            articleDetails: null,
            DOMPurify,
        }
    },

    computed: {
        // `null` until the app has resolved the final active language (see
        // src/utils/wpmlLocale.js); fetching before that would risk one
        // request with a provisional locale followed by another with the
        // real one. Once ready, this is either a WPML language code or ''
        // for Italian (WPML's default, which must be requested with no
        // `lang` param at all — see toWpmlLocale()).
        wpmlLang() {
            if (!localeReadiness.ready) return null;
            return toWpmlLocale(this.$i18n.locale) || '';
        }
    },

    methods: {
        async getArticle() {
            const loading = this.$vs.loading();
            const slugOrId = this.$route.params.articleId;

            try {
                const lang = this.wpmlLang ? '&lang=' + this.wpmlLang : '';

                // Try fetching by slug first (WP REST API)
                let url = wpBaseUrl() + '/wp-json/wp/v2/posts?slug=' + encodeURIComponent(slugOrId) + '&_embed' + lang;
                let response = await axios.get(url);

                let post = null;

                if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
                    post = response.data[0];
                } else {
                    // Try by ID if slug didn't match
                    url = wpBaseUrl() + '/wp-json/wp/v2/posts/' + encodeURIComponent(slugOrId) + '?_embed' + lang;
                    try {
                        response = await axios.get(url);
                        if (response.status === 200 && response.data) {
                            post = response.data;
                        }
                    } catch (e) {
                        // Post not found
                    }
                }

                if (post) {
                    this.articleDetails = this.mapWpPost(post);
                } else {
                    this.$vs.notification({
                        title: this.$t('common.messages.somethingWentWrong'),
                        text: this.$t('blog.messages.cannotLoadPost'),
                        color: 'danger',
                        position: 'top-right'
                    });
                }
            } catch (e) {
                console.error('Failed to load WP post:', e);
                this.$vs.notification({
                    title: this.$t('common.messages.somethingWentWrong'),
                    text: this.$t('blog.messages.cannotLoadPost'),
                    color: 'danger',
                    position: 'top-right'
                });
            }

            loading.close();
        },

        /**
         * Decode HTML entities (e.g. &#8217; → ') returned by WordPress REST API.
         */
        decodeHtmlEntities(str) {
            if (!str) return str;
            const txt = document.createElement('textarea');
            txt.innerHTML = str;
            return txt.value;
        },

        /**
         * Map a WP REST API post to the format expected by this component.
         */
        mapWpPost(post) {
            // Extract featured image URL from _embedded
            let pictureUrl = null;
            if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0]) {
                const media = post._embedded['wp:featuredmedia'][0];
                pictureUrl = media.source_url || null;
            }

            // Extract tags (post_tag taxonomy)
            const tags = [];
            if (post._embedded && post._embedded['wp:term']) {
                post._embedded['wp:term'].forEach(termGroup => {
                    termGroup.forEach(term => {
                        if (term.taxonomy === 'post_tag') {
                            tags.push(this.decodeHtmlEntities(term.name));
                        }
                    });
                });
            }

            // Extract meta description
            let metaDescription = '';
            if (post.yoast_head_json && post.yoast_head_json.description) {
                metaDescription = post.yoast_head_json.description;
            } else if (post.excerpt && post.excerpt.rendered) {
                metaDescription = post.excerpt.rendered.replace(/<[^>]+>/g, '').trim();
            }

            return {
                id: post.id,
                title: this.decodeHtmlEntities(post.title.rendered),
                text: post.content.rendered,
                slug: post.slug,
                updated_at: post.date,
                picture_url: pictureUrl,
                picture_id: null,
                tags: tags,
                meta_description: metaDescription,
                published: post.status === 'publish',
            };
        },
    },

    watch: {
        wpmlLang(n) {
            if (n == null) return; // still waiting for the locale to settle; mounted()/the readiness flip will fetch
            // Re-fetch so the WPML `lang` param follows the language the user just switched to.
            this.getArticle();
        }
    },

    mounted() {
        if (this.wpmlLang != null) {
            this.getArticle();
        }
    }
}
</script>

<style scoped>
.sx {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    justify-content: center;
}

h1 {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
}

.dx {
    display: flex;
    align-items: center;
    gap: 5px;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.post-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 20px;
    padding-left: 10vw;
    padding-right: 10vw;
    box-sizing: border-box;
}

.top {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.hero-img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 14px;
    object-fit: cover;
}

p,
h3 {
    margin: 0;
}

h3 {
    margin-top: 20px;
}


/* MOBILE */
@media (max-width: 600px) {
    .post-container {
        padding-top: 20px;
        padding-left: 20px;
        padding-right: 20px;
        box-sizing: border-box;
        padding-bottom: 80px!important;
        height: unset!important;
        width: 100%!important;
    }

    .header {
        flex-direction: column;
    }

    .dx {
        justify-content: flex-start;
        width: 100%;
    }

    h1 {
        font-size: 1.5rem;
    }
}
</style>


<style>
/* ── Content images ── */
.b-content figure,
.b-content .wp-block-image,
.b-content .wp-block-cover,
.b-content p:has(> img:only-child) {
    width: 100%;
    max-width: 100%;
    margin: 1.4rem 0;
}

.b-content figure figcaption {
    font-size: 0.8rem;
    opacity: 0.6;
    text-align: center;
    padding: 0.3rem 0 0;
}

.b-content img {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    border-radius: 10px;
    object-fit: cover;
    margin: 1.4rem 0;
}

.b-content figure img,
.b-content .wp-block-image img {
    border-radius: 10px;
    margin: 0;
}

/* ── Typography ── */
.b-content {
    font-size: 1rem;
    line-height: 1.8;
}

.b-content p {
    margin: 0.75rem 0;
}

.b-content h2 {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.3;
    margin-top: 2rem;
    margin-bottom: 0.5rem;
}

.b-content h3 {
    font-size: 1.15rem;
    font-weight: 600;
    line-height: 1.35;
    margin-top: 1.5rem;
    margin-bottom: 0.4rem;
}

.b-content h4 {
    font-size: 1.05rem;
    font-weight: 600;
    margin-top: 1.2rem;
    margin-bottom: 0.3rem;
}

.b-content a {
    text-decoration: none;
}

.b-content a:hover {
    text-decoration: underline;
}

.b-content ul,
.b-content ol {
    padding-left: 1.4rem;
    margin: 0.75rem 0;
}

.b-content li {
    margin: 0.3rem 0;
}

/* ── Tags ── */
.post-container .dx button {
    margin: 0;
}

/* ── Iframes (videos, maps) ── */
.b-content iframe {
    width: 100%!important;
    position: unset!important;
    height: 400px!important;
    max-width: 100%!important;
    border-radius: 10px;
    margin: 1rem 0;
}
</style>
